import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { CoinData } from '../../data/coinList';

interface PriceChartProps {
  coin: CoinData;
  timeframe: string;
  setTimeframe: (timeframe: string) => void;
}

const PriceChart: React.FC<PriceChartProps> = ({ coin, timeframe, setTimeframe }) => {
  // Generate mock chart data based on sparkline
  const generateChartData = () => {
    const prices = coin.sparkline_in_7d.price;
    const now = Date.now();
    const interval = timeframe === '24h' ? 60 * 60 * 1000 : // 1 hour
                    timeframe === '7d' ? 60 * 60 * 1000 : // 1 hour
                    timeframe === '30d' ? 4 * 60 * 60 * 1000 : // 4 hours
                    24 * 60 * 60 * 1000; // 1 day

    const dataPoints = timeframe === '24h' ? prices.slice(-24) :
                      timeframe === '7d' ? prices.slice(-168) :
                      timeframe === '30d' ? prices.slice(-180).filter((_, i) => i % 4 === 0) :
                      prices.slice(-365).filter((_, i) => i % 24 === 0);

    return dataPoints.map((price, index) => ({
      time: new Date(now - (dataPoints.length - index) * interval).toLocaleDateString(),
      price: price,
    }));
  };

  const chartData = generateChartData();
  const isPositive = coin.price_change_percentage_24h >= 0;

  const formatYAxis = (value: number) => {
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
    if (value >= 1) return `$${value.toFixed(0)}`;
    return `$${value.toFixed(4)}`;
  };

  const timeframes = [
    { label: '24H', value: '24h' },
    { label: '7D', value: '7d' },
    { label: '30D', value: '30d' },
    { label: '1Y', value: '1y' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">{coin.name} Price Chart</h2>
        <div className="flex space-x-2">
          {timeframes.map((tf) => (
            <button
              key={tf.value}
              onClick={() => setTimeframe(tf.value)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                timeframe === tf.value
                  ? 'bg-violet-500 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-64 sm:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={isPositive ? '#10B981' : '#EF4444'} stopOpacity={0.3} />
                <stop offset="95%" stopColor={isPositive ? '#10B981' : '#EF4444'} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
            <XAxis 
              dataKey="time" 
              stroke="#71717A"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#71717A"
              style={{ fontSize: '12px' }}
              tickFormatter={formatYAxis}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#18181B',
                border: '1px solid #27272A',
                borderRadius: '8px',
              }}
              labelStyle={{ color: '#A1A1AA' }}
              formatter={(value: number) => [`$${value.toFixed(2)}`, 'Price']}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke={isPositive ? '#10B981' : '#EF4444'}
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorPrice)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PriceChart;